import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, type PopperPlacementType } from '@mui/material';
import { AutocompletePrimitive, InputPrimitive } from '@figma/fpl-components';
import { Icon16Check, Icon24ChevronDown } from '@figma/fpl-icons';
import { useDropdownTheme } from '@figma/fpl-components/beta';
import { ColorTheme } from '@figma/fpl-themes';
import { borderRadius, color as token, shadow, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { I18N_KEYS } from '@/language/keys';
import { SearchIcon } from '@/UI/Icons/SearchIcon';
import { Typography, typographyStyles } from '../AppTypography/AppTypography';
import { getMaxHeight, isOptionLike } from '../BaseSelect/selectUtils';
import type { Option } from '../BaseSelect/useSelect';

export interface DropdownSearchFplProps<T, Opt extends Option<T>> {
  options: Opt[];
  value: Opt | T | null;
  confirmedValue?: Opt | T | null;
  onChange: (option: Opt) => void;
  onClose?: () => void;
  onOpen?: () => void;
  onHighlightedOptionChange?: (option: Opt | null) => void;
  helperText?: ReactNode;
  /** Rendered at the top of the open menu, above the options. */
  headerAction?: ReactNode;
  noOptionsText?: ReactNode;
  offset?: number;
  placement?: PopperPlacementType;
  size?: 'small' | 'large';
  disabled?: boolean;
  width?: string;
  matchTriggerWidth?: boolean;
  placeholder?: string;
  /** Placeholder for the search field when {@link searchInPopup} moves it into the menu. */
  searchPlaceholder?: string;
  /**
   * Keep the trigger as a closed select and render the search field inside the popup, under
   * `headerAction`. The popup is then wider than the trigger so the header and names fit.
   */
  searchInPopup?: boolean;
}

// Trigger chrome mirrors the FPL Select trigger (see fpl `select/select.css.ts`): a bordered,
// `color.bg` field rather than the softer FPL Input chrome.
const triggerStyles = stylex.create({
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${token.border}`,
    borderRadius: borderRadius.medium,
    background: token.bg,
    color: token.text,
    cursor: 'pointer',
    paddingLeft: spacer.spacer2,
    // Match the FPL Select trigger's keyboard-focus ring. The focusable element is the inner
    // `<input>`, so key off a descendant `:focus-visible` to ring the whole trigger box.
    outline: {
      default: 'none',
      ':has(:focus-visible)': `1px solid ${token.borderSelected}`,
    },
    outlineOffset: {
      default: 0,
      ':has(:focus-visible)': '-1px',
    },
  },
  md: { height: 'calc(24rem / 16)' },
  lg: { height: 'calc(32rem / 16)' },
  // While the menu is open, keep the selected-border ring regardless of focus source (mirrors the
  // open Select trigger).
  open: {
    outline: `1px solid ${token.borderSelected}`,
    outlineOffset: '-1px',
  },
  disabled: {
    color: token.textDisabled,
    cursor: 'default',
  },
  // Wraps the input so the collapsed ellipsis overlay can position over it.
  fieldWrap: {
    position: 'relative',
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  field: {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: 'inherit',
    padding: 0,
    cursor: 'inherit',
    textOverflow: 'ellipsis',
    '::placeholder': { color: token.textSecondary },
  },
  // Chromium never ellipsises a *focused* input, and the collapsed trigger keeps focus (so ArrowDown
  // reopens). While collapsed with a selection we hide the input's own text and paint an ellipsised
  // overlay instead, matching the FPL Select button trigger.
  fieldHiddenValue: { color: 'transparent' },
  overlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  overlayText: {
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  caret: {
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'transform 0.2s ease-in-out',
  },
  caretOpen: {
    transform: 'rotate(180deg)',
  },
  buttonReset: {
    font: 'inherit',
    textAlign: 'left',
  },
  placeholderText: {
    color: token.textSecondary,
  },
});

// Popup chrome mirrors the FPL Select menu (see fpl `select/select.css.ts` `container`): a borderless
// `bgMenu` surface with `radius.large`, `elevation400`, and vertical-only padding (option rows own
// their horizontal padding + inset highlight), rather than the Weave Menu chrome.
const popupStyles = stylex.create({
  // The rounded, elevated surface. Applied to the `PopupList`'s `ScrollContainer` root (the "upper
  // layer" that would otherwise show square corners behind the menu). `borderRadius`/`boxShadow` are
  // theme-independent tokens, so they resolve here even though this element is outside the portal
  // `ColorTheme`; `overflow: hidden` clips the square scroll viewport to the rounded shape.
  container: {
    borderRadius: borderRadius.large,
    boxShadow: shadow.elevation400,
    overflow: 'hidden',
    minWidth: 120,
    maxWidth: 400,
  },
  chrome: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    boxSizing: 'border-box',
    minWidth: 0,
    paddingTop: spacer.spacer2,
    paddingBottom: spacer.spacer2,
    background: token.bgMenu,
    color: token.text,
    borderRadius: borderRadius.large,
    overflowY: 'auto',
  },
  footer: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    padding: spacer.spacer12px,
    paddingBottom: 0,
    width: '100%',
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: spacer.spacer2,
    boxSizing: 'border-box',
    height: 'calc(32rem / 16)',
    marginLeft: spacer.spacer2,
    marginRight: spacer.spacer2,
    marginBottom: spacer.spacer2,
    paddingLeft: spacer.spacer2,
    paddingRight: spacer.spacer2,
    border: `1px solid ${token.border}`,
    borderRadius: borderRadius.medium,
    background: token.bg,
    color: token.textSecondary,
  },
  searchField: {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: token.text,
    padding: 0,
    '::placeholder': { color: token.textSecondary },
  },
});

// Styling the `AutocompletePrimitive.Option` directly (instead of nesting a Weave `MenuItem`, which
// isn't a valid FPL option child) so rows read as FPL Select options (see `select/select.css.ts`
// `option`): `bodyMedium` text with a `bgBrand` highlight drawn by an inset `::before` (inset 8px,
// `radius.medium`) on the active/selected row, and a trailing 16px selected/loading slot.
const optionStyles = stylex.create({
  root: {
    boxSizing: 'border-box',
    position: 'relative',
    isolation: 'isolate',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacer.spacer2,
    width: '100%',
    paddingLeft: spacer.spacer12px,
    paddingRight: spacer.spacer12px,
    color: token.text,
    cursor: 'pointer',
    userSelect: 'none',
    outline: 'none',
    '::before': {
      content: '""',
      position: 'absolute',
      zIndex: -1,
      top: 0,
      bottom: 0,
      left: spacer.spacer8px,
      right: spacer.spacer8px,
      borderRadius: borderRadius.medium,
      background: 'transparent',
    },
  },
  md: { minHeight: 'calc(24rem / 16)' },
  lg: { minHeight: 'calc(32rem / 16)' },
  active: {
    color: token.textOnbrand,
    '--fpl-icon-color': token.iconOnbrand,
    '::before': { background: token.bgBrand },
  },
  label: {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
  },
  trailing: {
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'calc(16rem / 16)',
    height: 'calc(16rem / 16)',
  },
});

type FplPlacement = NonNullable<Parameters<typeof AutocompletePrimitive.PopupList>[0]['placement']>;

const getOptionId = <T,>(option: Option<T>): string =>
  typeof option.id === 'function' ? option.id(option.value) : String(option.id);

/**
 * Flag-on searchable single-select built on the canonical `autocomplete-primitive` pattern (see the
 * `autocomplete-primitive` stories): `useAutocomplete` drives combobox a11y/keyboard (role=combobox
 * input + role=listbox/option, aria-activedescendant, arrow/Enter/Escape), the combobox input is the
 * popup anchor, and `AutocompletePrimitive.PopupList` owns positioning/portal/dismiss. The trigger is
 * an `InputPrimitive` styled like an FPL Select, and each option is a styled `AutocompletePrimitive.Option`
 * (no Weave `MenuItem`) so rows match FPL menu items. The consumer owns filtering state (mirrors
 * `useSelect.filteredOptions`).
 */
export const DropdownSearchFpl = <T, Opt extends Option<T>>({
  options,
  value,
  confirmedValue,
  onChange,
  onClose,
  onOpen,
  onHighlightedOptionChange,
  helperText,
  headerAction,
  noOptionsText,
  offset,
  placement,
  size = 'large',
  disabled = false,
  width = '100px',
  matchTriggerWidth = false,
  placeholder,
  searchPlaceholder,
  searchInPopup = false,
}: DropdownSearchFplProps<T, Opt>) => {
  const { t } = useTranslation();
  const inputId = useId();
  const anchorRef = useRef<HTMLElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // The query is seeded with the selected value on open; only filter once the user actually edits it,
  // so opening a dropdown with a selection still shows the full list (with the selection highlighted).
  const [queryDirty, setQueryDirty] = useState(false);
  // The popup renders in a portal, outside the app's ColorTheme.
  const themeMode = useThemeMode();
  const dropdownTheme = useDropdownTheme();
  const idToOption = useMemo(() => {
    const map = new Map<string, Opt>();
    options.forEach((option) => map.set(getOptionId(option), option));
    return map;
  }, [options]);

  const selectedOption = useMemo(() => {
    const current = confirmedValue ?? value;
    if (current == null) return undefined;
    return isOptionLike(current) ? current : options.find((o) => o.value === current);
  }, [confirmedValue, value, options]);

  const selectedId = selectedOption ? getOptionId(selectedOption) : undefined;
  const selected = useMemo(() => (selectedId ? [selectedId] : []), [selectedId]);

  const filterText = useCallback((option: Opt) => {
    if (typeof option.label === 'string') return option.label;
    if (typeof option.value === 'string') return option.value;
    return '';
  }, []);

  // The combobox input can only render a string; show the selected option's text (its label when a
  // string, else the filter text) when collapsed.
  const selectedLabelText = useMemo(() => {
    if (!selectedOption) return '';
    if (typeof selectedOption.label === 'string') return selectedOption.label;
    return filterText(selectedOption);
  }, [selectedOption, filterText]);

  const filteredOptions = useMemo(() => {
    const query = queryDirty ? searchQuery.trim().toLowerCase() : '';
    if (!query) return options;
    return options.filter((option) => filterText(option).toLowerCase().includes(query));
  }, [options, searchQuery, queryDirty, filterText]);

  const open = useCallback(() => {
    // Seed the query with the current selection so the trigger keeps showing the selected value
    // (not the "Search" placeholder) and the user can continue typing/editing from it. Not marked
    // dirty yet, so the full list stays visible until the user actually edits the text.
    // A menu that owns its own search field starts empty so the placeholder and full list show.
    setSearchQuery(searchInPopup ? '' : selectedLabelText);
    setQueryDirty(false);
    setExpanded(true);
    onOpen?.();
  }, [onOpen, searchInPopup, selectedLabelText]);

  const collapse = useCallback(() => {
    setExpanded(false);
    onClose?.();
  }, [onClose]);

  const handleExpandChange = useCallback(
    (next: boolean) => {
      if (next) open();
      else collapse();
    },
    [open, collapse],
  );

  const handleActiveValueChange = useCallback(
    (id: string | undefined) => {
      onHighlightedOptionChange?.(id != null ? (idToOption.get(id) ?? null) : null);
    },
    [onHighlightedOptionChange, idToOption],
  );

  const handleSelect = useCallback(
    (id: string | undefined) => {
      const option = id != null ? idToOption.get(id) : undefined;
      if (option) onChange(option);
      collapse();
    },
    [idToOption, onChange, collapse],
  );

  const manager = AutocompletePrimitive.useAutocomplete<string>({
    selected,
    expanded,
    onExpand: handleExpandChange,
    onSelect: handleSelect,
    onActiveValueChange: handleActiveValueChange,
    skipAutoSetFirstItemActive: true,
    expandOnFocus: false,
  });

  // Revert the ongoing preview when the popup closes (legacy fires onHighlightedOptionChange(null)
  // on close). autocomplete-primitive has no pointer-leave clear, so we clear the active value.
  const prevExpandedRef = useRef(false);
  useEffect(() => {
    const wasExpanded = prevExpandedRef.current;
    if (wasExpanded && !expanded) {
      manager.setActiveValue(undefined);
    } else if (!wasExpanded && expanded && selectedLabelText && !searchInPopup) {
      // Opened with a selection: highlight the whole seeded value so a single Backspace clears it.
      // Done post-commit (effect) so it isn't overridden by the click's default caret placement.
      manager.refs.input.current?.select();
    }
    prevExpandedRef.current = expanded;
  }, [expanded, searchInPopup, selectedLabelText, manager]);

  // The in-menu search field mounts in the popup portal after expand, and the tooltip focus manager
  // leaves focus on the trigger. Move it onto the field once that portal is in the document.
  useEffect(() => {
    if (!expanded || !searchInPopup) return;
    const frame = requestAnimationFrame(() => {
      const input = manager.refs.input.current;
      if (input && document.activeElement !== input) input.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [expanded, searchInPopup, manager]);

  const handleInputChange = useCallback((next: string) => {
    setSearchQuery(next);
    setQueryDirty(true);
  }, []);

  const onInputClick = useCallback(() => {
    if (disabled || expanded) return;
    open();
  }, [disabled, expanded, open]);

  const popupPlacement = useMemo<FplPlacement>(() => {
    // MUI's `auto*` placements have no Floating UI equivalent; the legacy default was `bottom`.
    if (!placement || placement.startsWith('auto')) return 'bottom-start';
    return placement as FplPlacement;
  }, [placement]);

  const maxHeight = getMaxHeight(true, !!helperText);
  const sizeKey = size === 'small' ? 'md' : 'lg';

  // `stylex.props` can emit its own `style` (e.g. for dynamic values), so merge it with the dynamic
  // `width` rather than letting a separate `style` prop clobber it.
  const triggerRootProps = stylex.props(
    triggerStyles.root,
    triggerStyles[sizeKey],
    expanded && triggerStyles.open,
    disabled && triggerStyles.disabled,
  );
  const popupSearchTriggerProps = stylex.props(
    triggerStyles.root,
    triggerStyles.buttonReset,
    triggerStyles[sizeKey],
    expanded && triggerStyles.open,
    disabled && triggerStyles.disabled,
  );

  // `PopupList` forwards `className`/`style` to its `ScrollContainer` root, so this rounds + elevates
  // the outermost popup layer and clips its (square) scroll viewport to the rounded corners.
  const popupRootProps = stylex.props(popupStyles.container);

  return (
    <>
      {searchInPopup ? (
        <button
          type="button"
          disabled={disabled}
          {...manager.getTriggerProps()}
          className={popupSearchTriggerProps.className}
          style={{ ...popupSearchTriggerProps.style, width }}
          ref={anchorRef as Ref<HTMLButtonElement>}
        >
          <span {...stylex.props(triggerStyles.fieldWrap)}>
            <span
              {...stylex.props(
                typographyStyles.textBodyMedium,
                triggerStyles.overlayText,
                !selectedLabelText && triggerStyles.placeholderText,
              )}
            >
              {selectedLabelText || placeholder}
            </span>
          </span>
          <span aria-hidden {...stylex.props(triggerStyles.caret, expanded && triggerStyles.caretOpen)}>
            <Icon24ChevronDown />
          </span>
        </button>
      ) : (
        /* The FPL `InputPrimitive` is the combobox trigger + popup anchor (see the autocomplete-primitive
          stories). It forwards the `getInputProps` combobox wiring (role/aria/keyboard) straight to the
          native input. The Root is styled to look like an FPL Select trigger rather than an Input. */
        <InputPrimitive.Root
          ref={anchorRef as Ref<HTMLDivElement>}
          className={triggerRootProps.className}
          style={{ ...triggerRootProps.style, width }}
        >
          <span {...stylex.props(triggerStyles.fieldWrap)}>
            <InputPrimitive
              {...manager.getInputProps({
                id: inputId,
                value: expanded ? searchQuery : selectedLabelText,
                onChange: handleInputChange,
                onClick: onInputClick,
                placeholder: placeholder ?? (expanded ? t(I18N_KEYS.GENERAL.SEARCH) : ''),
                disabled,
                readOnly: !expanded,
              })}
              {...stylex.props(
                typographyStyles.textBodyMedium,
                triggerStyles.field,
                !expanded && !!selectedLabelText && triggerStyles.fieldHiddenValue,
              )}
            />
            {!expanded && selectedLabelText ? (
              <span aria-hidden {...stylex.props(triggerStyles.overlay)}>
                <span {...stylex.props(typographyStyles.textBodyMedium, triggerStyles.overlayText)}>
                  {selectedLabelText}
                </span>
              </span>
            ) : null}
          </span>
          <span aria-hidden {...stylex.props(triggerStyles.caret, expanded && triggerStyles.caretOpen)}>
            <Icon24ChevronDown />
          </span>
        </InputPrimitive.Root>
      )}
      <AutocompletePrimitive.PopupList
        anchorEl={anchorRef}
        placement={popupPlacement}
        offset={offset ?? 4}
        {...manager.getListProps()}
        className={popupRootProps.className}
        style={{
          ...popupRootProps.style,
          ...(matchTriggerWidth ? { width, minWidth: width, maxWidth: width } : undefined),
        }}
      >
        {/* Match FPL Select: menus default to dark, with an explicit match-theme override. */}
        <ColorTheme brand="weave" mode={dropdownTheme === 'dark' ? 'dark' : themeMode}>
          <div
            {...stylex.props(popupStyles.chrome)}
            style={{ maxHeight, ...(headerAction ? { paddingTop: 0 } : undefined) }}
          >
            {headerAction}
            {searchInPopup ? (
              <div {...stylex.props(popupStyles.search)} onPointerDown={(event) => event.stopPropagation()}>
                <SearchIcon width={16} height={16} />
                <InputPrimitive
                  {...manager.getInputProps({
                    id: inputId,
                    value: searchQuery,
                    onChange: handleInputChange,
                    placeholder: searchPlaceholder ?? t(I18N_KEYS.GENERAL.SEARCH),
                  })}
                  {...stylex.props(typographyStyles.textBodyMedium, popupStyles.searchField)}
                />
              </div>
            ) : null}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const id = getOptionId(option);
                const isSelected = selectedId === id;
                return (
                  <AutocompletePrimitive.Option
                    key={id}
                    value={id}
                    {...stylex.props(
                      typographyStyles.textBodyMedium,
                      optionStyles.root,
                      optionStyles[sizeKey],
                      manager.activeValue === id && optionStyles.active,
                    )}
                  >
                    <span {...stylex.props(optionStyles.label)}>{option.label}</span>
                    <span {...stylex.props(optionStyles.trailing)} title={isSelected ? 'Selected' : undefined}>
                      {isSelected && <Icon16Check aria-hidden />}
                    </span>
                  </AutocompletePrimitive.Option>
                );
              })
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', px: 1.5, py: 1 }}>
                <Typography variant="body-sm-rg" color={token.textSecondary} sx={{ textAlign: 'center' }}>
                  {noOptionsText || t(I18N_KEYS.COMMON_COMPONENTS.BASE_SELECT.NO_OPTIONS_TEXT)}
                </Typography>
              </Box>
            )}
            {helperText ? (
              <div {...stylex.props(popupStyles.footer)}>
                <Typography variant="body-xs-rg" color={token.textSecondary}>
                  {helperText}
                </Typography>
              </div>
            ) : null}
          </div>
        </ColorTheme>
      </AutocompletePrimitive.PopupList>
    </>
  );
};
